/** @domain award -- Award lifecycle fields: grant, vesting, PSU performance, option terms, settlement, election, adjustment. */
import { z } from 'zod';
import { createDomainField } from './types';
import { commaFormatter, limitDecimalPlaces, wholeNumberOnly } from './formatters';

const define = createDomainField('award');

// ---------------------------------------------------------------------------
// Core award fields
// ---------------------------------------------------------------------------

export const awardNumber = define({
  label: 'Award Number',
  placeholder: 'Enter award number',
  schema: z
    .string()
    .max(100, 'Must be at most 100 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank'),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const awardStatus = define({
  label: 'Award Status',
  schema: z.string(),
  adapterHint: 'text',
  displayFormat: 'status',
  statusMap: {
    'Fully Vested': { tone: 'primary' },
    Outstanding: { tone: 'success' },
    _default: { tone: 'error' },
  },
});

export const quantity = define({
  label: 'Quantity',
  placeholder: 'Enter quantity',
  schema: z.string().regex(/^[0-9,]*$/, 'Must contain only digits'),
  adapterHint: 'text',
  formatter: commaFormatter,
  displayFormat: 'number',
});

export const grantReason = define({
  label: 'Grant Reason',
  placeholder: 'Enter grant reason',
  schema: z.string().max(500, 'Must be at most 500 characters'),
  adapterHint: 'textarea',
  displayFormat: 'text',
});

export const isAccepted = define({
  label: 'Is Accepted',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'status',
  statusMap: {
    true: { tone: 'success', label: 'Accepted' },
    false: { tone: 'info', label: 'Pending' },
    _default: { tone: 'info', label: 'Pending' },
  },
});

// ---------------------------------------------------------------------------
// Grant fields
// ---------------------------------------------------------------------------

export const grantName = define({
  label: 'Grant Name',
  placeholder: 'Enter grant name',
  schema: z
    .string()
    .max(200, 'Must be at most 200 characters')
    .regex(/^\s*\S[\s\S]*$/, 'Must not be blank'),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const grantId = define({
  label: 'Grant',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const approvalRequired = define({
  label: 'Approval Required',
  schema: z.boolean(),
  adapterHint: 'checkbox',
  displayFormat: 'boolean',
});

export const acceptanceMethod = define({
  label: 'Acceptance Method',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

// ---------------------------------------------------------------------------
// Vesting fields
// ---------------------------------------------------------------------------

export const vestingTemplateName = define({
  label: 'Vesting Template',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const vestingTypeId = define({
  label: 'Vesting Type',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const loadingTypeId = define({
  label: 'Loading Type',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const roundingTypeId = define({
  label: 'Rounding Type',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const lengthInMonths = define({
  label: 'Full Vesting Period (months)',
  placeholder: 'Enter months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const intervalInMonths = define({
  label: 'Vesting Frequency (months)',
  placeholder: 'Enter months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const vestsAfterInMonths = define({
  label: 'Cliff Period (months)',
  placeholder: 'Enter cliff months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const cliffPercentage = define({
  label: 'Cliff Percentage',
  placeholder: 'Enter cliff %',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,2})?$/, 'Must be a valid percentage'),
  adapterHint: 'number',
  formatter: limitDecimalPlaces(2),
  displayFormat: 'percentage',
});

export const vestAtEndOfMonth = define({
  label: 'Vest At End Of Month',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const specificVestingInstruction = define({
  label: 'Specific Vesting Instruction',
  placeholder: 'Enter instruction',
  schema: z.string().max(1000, 'Must be at most 1000 characters'),
  adapterHint: 'text',
  displayFormat: 'text',
});

export const vestingSummary = define({
  label: 'Vesting Summary',
  placeholder: 'Enter vesting summary',
  schema: z.string().max(2000, 'Must be at most 2000 characters'),
  adapterHint: 'textarea',
  displayFormat: 'text',
});

// ---------------------------------------------------------------------------
// Performance (PSU) fields
// ---------------------------------------------------------------------------

export const performanceRuleName = define({
  label: 'Performance Rule',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const ruleTypeId = define({
  label: 'Rule Type',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const minimumPercentage = define({
  label: 'Minimum Percentage',
  placeholder: 'Enter minimum %',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,2})?$/, 'Must be a valid percentage'),
  adapterHint: 'number',
  formatter: limitDecimalPlaces(2),
  displayFormat: 'percentage',
});

export const maximumPercentage = define({
  label: 'Maximum Percentage',
  placeholder: 'Enter maximum %',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,2})?$/, 'Must be a valid percentage'),
  adapterHint: 'number',
  formatter: limitDecimalPlaces(2),
  displayFormat: 'percentage',
});

export const multiplierPercentage = define({
  label: 'Multiplier Percentage',
  placeholder: 'Enter multiplier %',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,2})?$/, 'Must be a valid percentage'),
  adapterHint: 'number',
  formatter: limitDecimalPlaces(2),
  displayFormat: 'percentage',
});

export const earnedMultiplier = define({
  label: 'Earned Multiplier',
  placeholder: 'Enter earned multiplier %',
  schema: z.string().regex(/^(\d+|\d{1,3}(,\d{3})*)(\.\d{0,2})?$/, 'Must be a valid percentage'),
  adapterHint: 'number',
  formatter: limitDecimalPlaces(2),
  displayFormat: 'percentage',
});

export const showRuleToParticipant = define({
  label: 'Show Rule To Participant',
  schema: z.boolean(),
  adapterHint: 'checkbox',
  displayFormat: 'boolean',
});

export const showTrackingToParticipant = define({
  label: 'Show Tracking To Participant',
  schema: z.boolean(),
  adapterHint: 'checkbox',
  displayFormat: 'boolean',
});

// ---------------------------------------------------------------------------
// Option-specific fields
// ---------------------------------------------------------------------------

export const optionExpirationInMonths = define({
  label: 'Expiration (months)',
  placeholder: 'Enter months',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const optionExpirationInDays = define({
  label: 'Expiration (days)',
  placeholder: 'Enter days',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const isEarlyExerciseEnabled = define({
  label: 'Early Exercise Enabled',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const isUnvestedAcquisition = define({
  label: 'Unvested Acquisition (RSA)',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

// ---------------------------------------------------------------------------
// Settlement fields
// ---------------------------------------------------------------------------

export const settlementTypeId = define({
  label: 'Settlement Type',
  schema: z.string(),
  adapterHint: 'async-select',
  displayFormat: 'text',
});

export const settlementMethod = define({
  label: 'Settlement Method',
  schema: z.array(z.string()),
  adapterHint: 'multi-select',
  displayFormat: 'text',
});

export const defaultSettlementTypeId = define({
  label: 'Default Settlement Type',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

// ---------------------------------------------------------------------------
// Election window fields
// ---------------------------------------------------------------------------

export const electionWindowStartDays = define({
  label: 'Election Window Start (days from vesting)',
  placeholder: 'Enter days',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

export const electionWindowEndDays = define({
  label: 'Election Window End (days from vesting)',
  placeholder: 'Enter days',
  schema: z.string().regex(/^[0-9]*$/, 'Must be a whole number'),
  adapterHint: 'number',
  formatter: wholeNumberOnly,
  displayFormat: 'number',
});

// ---------------------------------------------------------------------------
// Award adjustment fields
// ---------------------------------------------------------------------------

export const adjustmentMethodType = define({
  label: 'Acceleration Type',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const cancelShareTypeId = define({
  label: 'Cancel Share Type',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const targetOutstanding = define({
  label: 'Target Outstanding',
  placeholder: 'Enter target outstanding',
  schema: z.string().regex(/^[0-9,]*$/, 'Must contain only digits'),
  adapterHint: 'text',
  formatter: commaFormatter,
  displayFormat: 'number',
});

export const targetCancelled = define({
  label: 'Target Cancelled',
  placeholder: 'Enter target cancelled',
  schema: z.string().regex(/^[0-9,]*$/, 'Must contain only digits'),
  adapterHint: 'text',
  formatter: commaFormatter,
  displayFormat: 'number',
});

export const adjustmentReason = define({
  label: 'Adjustment Reason',
  placeholder: 'Enter adjustment reason',
  schema: z.string().max(500, 'Must be at most 500 characters'),
  adapterHint: 'textarea',
  displayFormat: 'text',
});

export const awardAdjustmentMethodId = define({
  label: 'Award Adjustment Method',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});
